import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useToast } from '../../../components/ui/Toast';
import { conferenceService } from '../../../services/conferenceService';
import { formatDate } from '../../../utils/formatters';

/**
 * Conference Management → Badges tab.
 *
 * Deliberately narrow: the badge layout is fixed code, shared with the real
 * attendee download (renderBadgePdf), so what's previewed here is exactly
 * what gets generated for real. A chair can only adjust a header line and
 * two show/hide toggles — not colors, fonts or positioning — because this is
 * a feature touched a handful of times a year, and a full badge designer
 * would be a lot of surface area for that.
 */
const SAMPLE_ATTENDEE = {
  name: 'Jane Doe',
  institution: 'Sample University',
  participantId: 'CONF-0001',
};

const BadgesTab = ({ conferenceId }) => {
  const { t } = useTranslation('conferences');
  const { success: toastSuccess, error: toastError } = useToast();

  const [conference, setConference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ headerText: '', showInstitution: true, showQr: true });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewUrlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const conf = await conferenceService?.getById(conferenceId);
        if (cancelled) return;
        setConference(conf);
        setForm({
          headerText: conf?.badgeHeaderText || '',
          showInstitution: conf?.badgeShowInstitution ?? true,
          showQr: conf?.badgeShowQr ?? true,
        });
      } catch (err) {
        console.error('Load conference for badges tab error:', err);
        toastError(t('badges.loadFailed', 'Could not load conference settings.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conferenceId]);

  // Re-render the preview whenever the form or the conference's own data
  // changes, debounced so typing in the header field doesn't re-render a PDF
  // on every keystroke.
  useEffect(() => {
    if (!conference) return;
    const timer = setTimeout(() => generatePreview(), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conference, form]);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const generatePreview = async () => {
    setPreviewLoading(true);
    try {
      const bannerUrl = conference?.bannerImageUrl
        ? await conferenceService?.getBannerImageUrl(conference.bannerImageUrl)
        : null;

      const dateRange = conference?.startDate
        ? conference?.endDate && conference.endDate !== conference.startDate
          ? `${formatDate(conference.startDate)} – ${formatDate(conference.endDate)}`
          : formatDate(conference.startDate)
        : '';

      const { renderBadgePdf } = await import('../../../lib/badge/renderBadge');
      const bytes = await renderBadgePdf({
        attendee: SAMPLE_ATTENDEE,
        roleLabel: t('badges.sampleRole', 'Chair'),
        conference: { name: conference?.name, bannerImageUrl: bannerUrl, dateRange },
        settings: { headerText: form.headerText, showInstitution: form.showInstitution, showQr: form.showQr },
      });

      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch (err) {
      console.error('Badge preview error:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await conferenceService?.update(conferenceId, {
        badgeHeaderText: form.headerText?.trim() || null,
        badgeShowInstitution: form.showInstitution,
        badgeShowQr: form.showQr,
      });
      toastSuccess(t('badges.saved', 'Badge settings saved.'));
    } catch (err) {
      console.error('Save badge settings error:', err);
      toastError(t('badges.saveFailed', 'Could not save badge settings.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Icon name="Loader" size={28} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-sm font-semibold text-foreground">{t('badges.heading', 'Attendee badges')}</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {t(
            'badges.subheading',
            'Anyone accepted, chairing, organizing or reviewing for this conference can download their own badge from their dashboard. Adjust what shows on it below — the preview updates as you type.'
          )}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4 bg-card border border-border rounded-lg p-4">
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">
              {t('badges.headerTextLabel', 'Header line')}
            </label>
            <Input
              value={form.headerText}
              onChange={(e) => setForm((f) => ({ ...f, headerText: e?.target?.value }))}
              placeholder={conference?.name || t('badges.headerTextPlaceholder', 'e.g. Welcome to ICML 2026')}
              maxLength={80}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('badges.headerTextHelp', 'Leave blank to just use the conference name.')}
            </p>
          </div>

          <label className="flex items-center justify-between gap-3 py-1 cursor-pointer">
            <span className="text-sm text-foreground">{t('badges.showInstitution', 'Show institution')}</span>
            <input
              type="checkbox"
              checked={form.showInstitution}
              onChange={(e) => setForm((f) => ({ ...f, showInstitution: e?.target?.checked }))}
              className="h-4 w-4"
            />
          </label>

          <label className="flex items-center justify-between gap-3 py-1 cursor-pointer">
            <span className="text-sm text-foreground">{t('badges.showQr', 'Show QR code')}</span>
            <input
              type="checkbox"
              checked={form.showQr}
              onChange={(e) => setForm((f) => ({ ...f, showQr: e?.target?.checked }))}
              className="h-4 w-4"
            />
          </label>

          <div className="pt-2">
            <Button variant="default" onClick={handleSave} loading={saving} iconName="Save" iconPosition="left">
              {t('badges.save', 'Save badge settings')}
            </Button>
          </div>
        </div>

        <div className="bg-muted/30 border border-border rounded-lg p-4 flex flex-col items-center">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3 self-start">
            {t('badges.previewLabel', 'Preview (sample data)')}
          </p>
          {previewUrl ? (
            <iframe
              key={previewUrl}
              src={previewUrl}
              title="Badge preview"
              className={`w-full rounded-md border border-border bg-white transition-opacity ${previewLoading ? 'opacity-50' : 'opacity-100'}`}
              style={{ aspectRatio: '2 / 3', minHeight: 420 }}
            />
          ) : (
            <div className="w-full flex items-center justify-center text-muted-foreground" style={{ aspectRatio: '2 / 3', minHeight: 420 }}>
              <Icon name="Loader" size={24} className="animate-spin" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BadgesTab;
